import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

import { checkSpanishLocale, cn } from 'lib/utils';
import { useSymptomStore } from 'store/symptom';
import SymptomContainer from './components/SymptomContainer';
import useSymptomChecker from 'hooks/useSymptomChecker';
import Icon from 'components/icon/Icon';
import Checkmark from 'pages/profile/questionnaire/components/Checkmark';
import { EMOTION_ICON_COLORS } from 'lib/constants';
import { Button } from 'components/ui/button';

export default function SymptomPainLevel() {
  const questionData = useSymptomStore((store) => store.questionData)?.data;

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useSymptomChecker();

  const [levelIndex, setLevelIndex] = useState<number>(0);
  const [loadingChoice, setLoadingChoice] = useState<string>('');

  const englishChoices = JSON.parse(questionData?.eng_choices || '[]');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '[]');

  const moodOptions = isSpanish ? spanishChoices : englishChoices;

  const moods = useMemo(
    () => [
      { icon: 'depressed', label: moodOptions[5] },
      { icon: 'sad', label: moodOptions[4] },
      { icon: 'neutral', label: moodOptions[3] },
      { icon: 'happy', label: moodOptions[2] },
      { icon: 'overjoyed', label: moodOptions[1] },
    ],
    [moodOptions]
  );

  useEffect(() => {
    const getInitialIndex = () => {
      if (
        questionData?.user_eng_choices ||
        questionData?.user_spanish_choices
      ) {
        const selected =
          questionData?.[
            isSpanish ? 'user_spanish_choices' : 'user_eng_choices'
          ];
        const enChoices = JSON.parse(questionData?.eng_choices || '');
        const esChoices = JSON.parse(questionData?.spanish_choices || '');
        const options = isSpanish ? esChoices : enChoices;
        const selectedIndex = [...options].reverse().indexOf(selected);

        return selectedIndex > 0 && selectedIndex <= options.length - 2
          ? selectedIndex
          : 3;
      }

      return 3;
    };

    const index = getInitialIndex();

    setLevelIndex(index);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData, isSpanish]);

  const onContinue = () => {
    setLoadingChoice('yes');
    const reversedEnChoices = [...englishChoices].reverse();
    const reversedEsChoices = [...spanishChoices].reverse();
    const englishChoice = reversedEnChoices[levelIndex];
    const spanishChoice = reversedEsChoices[levelIndex];

    onSubmit({
      eng_choices: englishChoice,
      spanish_choices: spanishChoice,
      questionData,
    });
  };

  const onNoSelect = () => {
    setLoadingChoice('no');

    onSubmit({
      eng_choices: englishChoices[0],
      spanish_choices: spanishChoices[0],
      questionData,
    });
  };

  return (
    <SymptomContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={3}
      otherButton={
        <Button
          variant='outline'
          className='px-6 text-gray-500 border-gray-500 h-9'
          onClick={onNoSelect}
          disabled={loadingChoice === 'no'}
        >
          {loadingChoice === 'no' && (
            <Loader2 className='mr-2 animate-spin size-4' />
          )}
          {moodOptions[0]}
        </Button>
      }
    >
      <div className='grid grid-cols-1 gap-4 lg:gap-6 lg:grid-cols-2'>
        {moods.map((mood, index) => {
          const isSelected = index === levelIndex;
          const selectedColor =
            EMOTION_ICON_COLORS[index % EMOTION_ICON_COLORS.length];

          return (
            <motion.div
              key={mood.icon}
              onClick={() => setLevelIndex(index)}
              className={cn(
                'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-[#656D76] gap-4',
                isSelected && 'border-primary text-primary bg-[#E1EBFF]'
              )}
              whileTap={{ scale: 0.98 }}
            >
              <div className='flex items-center gap-2'>
                <div className='shrink-0'>
                  <Icon
                    icon={mood.icon}
                    size={48}
                    color={isSelected ? selectedColor : '#656D76'}
                  />
                </div>
                <p className='text-sm font-medium leading-5'>{mood?.label}</p>
              </div>

              <Checkmark isSelected={isSelected} />
            </motion.div>
          );
        })}
      </div>
    </SymptomContainer>
  );
}
